import React, { useMemo, useEffect, useCallback, useState } from 'react'
import './Filters.css'
import { useSearchParams } from 'react-router-dom'

// Size groups matching AddProduct
const SIZE_GROUPS = [
    { label: 'Letter',      sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'] },
    { label: 'Waist (in.)', sizes: ['28', '30', '32', '34', '36', '38', '40', '42'] },
    { label: 'Shoe (UK)',   sizes: ['6', '7', '8', '9', '10', '11', '12'] },
]

// Only these predefined tags show in the customer filter — custom tags are excluded
const PRESET_TAGS = ['New', 'Popular', 'Trending', 'Sale', 'Featured', 'Limited Edition']
 
const LIGHT_COLORS = ['#FFFFFF','#FFFDD0','#F5F0DC','#FDD835','#C6EF3B','#D3D3D3']

// sorting options
const SORT_OPTIONS = [
    { value: 'newest',      label: 'Newest First' },
    { value: 'price_asc',   label: 'Price: Low to High' },
    { value: 'price_desc',  label: 'Price: High to Low' },
    { value: 'name_asc',    label: 'Name: A → Z' },
]

const Filters = ({ products, lockedCategory, onFilteredChange }) => {

    const [searchParams, setSearchParams] = useSearchParams()

    const [availableSubCategories, setAvailableSubCategories] = useState([])

    // Read current filter state from URL
    const selectedCategories = useMemo(() =>
        searchParams.get('category')?.split(',').filter(Boolean) || []
    , [searchParams])

    const selectedColors = useMemo(() =>
        searchParams.get('colors')?.split(',').filter(Boolean) || []
    , [searchParams])
 
    const selectedSizes = useMemo(() =>
        searchParams.get('sizes')?.split(',').filter(Boolean) || []
    , [searchParams])

    const selectedTags = useMemo(() =>
        searchParams.get('tags')?.split(',').filter(Boolean) || []
    , [searchParams])

    const selectedSubCategory = useMemo(() =>
        searchParams.get('subCategory') || ''
    , [searchParams])
 
    const searchQuery = useMemo(() =>
        searchParams.get('search')?.trim() || ''
    , [searchParams])
 
    const minPrice = useMemo(() =>
        searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : null
    , [searchParams])
 
    const maxPrice = useMemo(() =>
        searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : null
    , [searchParams])

    const sort = useMemo(() =>
        searchParams.get('sort') || 'newest'
    , [searchParams])

    const activeSingleCategory = useMemo(() => {
        if (lockedCategory) return lockedCategory
        if (selectedCategories.length === 1) return selectedCategories[0]
        return null
    }, [lockedCategory, selectedCategories])

    // Fetch subcategories when the active single category changes
    useEffect(() => {
        if (!activeSingleCategory) {
            setAvailableSubCategories([])
            return
        }
        fetch(`${process.env.REACT_APP_API_URL}/subcategories?category=${activeSingleCategory}`)
            .then(r => r.json())
            .then(d => {
                if (d.success) setAvailableSubCategories(d.subcategories)
            })
            .catch(console.error)
    }, [activeSingleCategory])

    // Clear subCategory from URL if we switch to a different category
    useEffect(() => {
        if (selectedSubCategory && !activeSingleCategory) {
            setParam('subCategory', null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSingleCategory, selectedSubCategory])

    // Derive available options from the product list
    const { allColors, allTags, priceMin, priceMax, availableCategories } = useMemo(() => {
        const colorMap = {}
        const tagSet = new Set()
        const catSet = new Set()
        let low = Infinity
        let high = 0
 
        products.forEach(product => {
            catSet.add(product.category)
            if (product.price < low)  low  = product.price
            if (product.price > high) high = product.price;
            (product.colors || []).forEach(color => { colorMap[color.hex] = color })
            ;(product.tags || []).forEach(tag => tagSet.add(tag))
        })
 
        return {
            availableCategories: [...catSet].sort(),
            allColors: Object.values(colorMap),
            allTags: PRESET_TAGS.filter(tag => tagSet.has(tag)),
            priceMin: low  === Infinity ? 0 : Math.floor(low),
            priceMax: high === 0 ? 9999 : Math.ceil(high),
        }
    }, [products])

    // apply filters
    const applyFilters = useCallback(() => {
        let result = [...products]

        // Search — matches name, category or storeProductId
        if (searchQuery) {
            const q = searchQuery.toLowerCase()
            result = result.filter(p =>
                p.name?.toLowerCase().includes(q) ||
                p.category?.toLowerCase().includes(q) ||
                p.storeProductId?.toLowerCase().includes(q)
            )
        }

        // Category — if locked, the parent already pre-filtered; skip
        if (!lockedCategory && selectedCategories.length > 0) {
            result = result.filter(p => selectedCategories.includes(p.category))
        }

        // SubCategory — AND with category, scoped to the active category
        if (selectedSubCategory) {
            result = result.filter(p => p.subCategory === selectedSubCategory)
        }

        // Colors — OR within group
        if (selectedColors.length > 0) {
            result = result.filter(p =>
                (p.colors || []).some(c => selectedColors.includes(c.name))
            )
        }
 
        // Sizes — OR within group
        if (selectedSizes.length > 0) {
            result = result.filter(p =>
                (p.sizes || []).some(s => selectedSizes.includes(s))
            )
        }

        // Tags — OR within group
        if (selectedTags.length > 0) {
            result = result.filter(p =>
                (p.tags || []).some(t => selectedTags.includes(t))
            )
        }
 
        // Price range
        const lo = minPrice ?? priceMin
        const hi = maxPrice ?? priceMax
        result = result.filter(p => p.price >= lo && p.price <= hi)

        // Sort
        result.sort((a, b) => {
            if (sort === 'price_asc')  return a.price - b.price
            if (sort === 'price_desc') return b.price - a.price
            if (sort === 'name_asc')   return a.name.localeCompare(b.name)
            // newest — sort by date desc
            return new Date(b.date) - new Date(a.date)
        })

        onFilteredChange(result, sort)
    }, [products, selectedCategories, selectedSubCategory, selectedColors, selectedSizes, selectedTags,
        minPrice, maxPrice, sort, priceMin, priceMax, lockedCategory, onFilteredChange, searchQuery])

    useEffect(() => { applyFilters() }, [applyFilters])

    // URL helpers 
    const setParam = (key, value) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            if (!value || (Array.isArray(value) && value.length === 0)) {
                next.delete(key)
            } else {
                next.set(key, Array.isArray(value) ? value.join(',') : String(value))
            }
            return next
        }, { replace: true })
    }

    const toggleInList = (current, value) =>
        current.includes(value) ? current.filter(v => v !== value) : [...current, value]
 
    const clearAll = () => {
        setSearchParams(lockedCategory ? { category: lockedCategory } : {}, { replace: true })
    }

    const hasActiveFilters =
        !!searchQuery ||
        !!selectedSubCategory ||
        selectedColors.length > 0 ||
        selectedSizes.length > 0 ||
        selectedTags.length > 0 ||
        minPrice !== null || maxPrice !== null ||
        (!lockedCategory && selectedCategories.length > 0)

    return (
        <aside className='filters'>
            {/* header */}
            <div className="filters-header">
                <h2>Filters</h2>
                {hasActiveFilters && (
                    <button className="filters-clear-btn" onClick={clearAll}>Clear all</button>
                )}
            </div>

            {/* Active search indicator */}
            {searchQuery && (
                <div className="filter-section">
                    <h3 className="filter-section-title">Search</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', color: '#333' }}>"{searchQuery}"</span>
                        <button
                            className="filter-reset-inline"
                            onClick={() => setParam('search', null)}
                        >✕ Clear</button>
                    </div>
                </div>
            )}

            {/* sort */}
            <div className="filter-section">
                <h3 className="filter-section-title">Sort By</h3>
                <div className="filter-sort-options">
                    {SORT_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            className={`filter-sort-btn ${sort === opt.value ? 'filter-sort-btn--active' : ''}`}
                            onClick={() => setParam('sort', opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* tags */}
            {allTags.length > 0 && (
                <div className="filter-section">
                    {/* <h3 className="filter-section-title">
                        Collection
                        {selectedTags.length > 0 && (
                            <span className="filter-active-count">{selectedTags.length}</span>
                        )}
                    </h3> */}
                    <div className="filter-tags-grid">
                        {allTags.map(tag => (
                            <button
                                key={tag}
                                className={`filter-tag-btn ${selectedTags.includes(tag) ? 'filter-tag-btn--active' : ''}`}
                                onClick={() => setParam('tags', toggleInList(selectedTags, tag))}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* category — hidden if locked */}
            {!lockedCategory ? (
                <div className="filter-section">
                    <h3 className="filter-section-title">Category</h3>
                    <div className="filter-checkboxes">
                        {availableCategories.map(cat => (
                            <label key={cat} className="filter-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={selectedCategories.includes(cat)}
                                    onChange={() => {
                                        // Clear subCategory when category changes
                                        setParam('subCategory', null)
                                        setParam('category', toggleInList(selectedCategories, cat))
                                    }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{cat}</span>
                            </label>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="filter-section">
                    <h3 className="filter-section-title">Category</h3>
                    <div className="filter-locked-pill">
                        <span style={{ textTransform: 'capitalize' }}>{lockedCategory}</span>
                        <span className="filter-locked-icon">🔒</span>
                    </div>
                    <p className="filter-locked-hint">Browsing {lockedCategory} category</p>
                </div>
            )}

            {/* Subcategory — only shown when a single category is active */}
            {availableSubCategories.length > 0 && (
                <div className="filter-section">
                    <h3 className="filter-section-title">
                        Subcategory
                        {selectedSubCategory && (
                            <span className="filter-active-count">1</span>
                        )}
                    </h3>
                    <div className="filter-checkboxes">
                        {availableSubCategories.map(sub => (
                            <label key={sub._id} className="filter-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={selectedSubCategory === sub.name}
                                    onChange={() =>
                                        setParam('subCategory',
                                            selectedSubCategory === sub.name ? null : sub.name
                                        )
                                    }
                                />
                                <span>{sub.name}</span>
                            </label>
                        ))}
                    </div>
                    {selectedSubCategory && (
                        <button
                            className="filter-reset-inline"
                            onClick={() => setParam('subCategory', null)}
                        >↺ Clear subcategory</button>
                    )}
                </div>
            )}

            {/* price range */}
            <div className="filter-section">
                <h3 className="filter-section-title">Price Range</h3>
                <div className="filter-price-row">
                    <div className="filter-price-input-wrap">
                        <label>Min</label>
                        <input
                            type="number"
                            className="filter-price-input"
                            placeholder={priceMin}
                            value={minPrice ?? ''}
                            min={priceMin}
                            max={priceMax}
                            onChange={e => setParam('minPrice', e.target.value || null)}
                        />
                    </div>
                    <span className="filter-price-dash">—</span>
                    <div className="filter-price-input-wrap">
                        <label>Max</label>
                        <input
                            type="number"
                            className="filter-price-input"
                            placeholder={priceMax}
                            value={maxPrice ?? ''}
                            min={priceMin}
                            max={priceMax}
                            onChange={e => setParam('maxPrice', e.target.value || null)}
                        />
                    </div>
                </div>
                {(minPrice !== null || maxPrice !== null) && (
                    <button
                        className="filter-reset-inline"
                        onClick={() => { setParam('minPrice', null); setParam('maxPrice', null) }}
                    >↺ Reset price</button>
                )}
            </div>

            {/* colors */}
            {allColors.length > 0 && (
                <div className="filter-section">
                    <h3 className="filter-section-title">
                        Color
                        {selectedColors.length > 0 && (
                            <span className="filter-active-count">{selectedColors.length}</span>
                        )}
                    </h3>
                    <div className="filter-color-grid">
                        {allColors.map(color => {
                            const isLight    = LIGHT_COLORS.includes(color.hex)
                            const isSelected = selectedColors.includes(color.name)
                            return (
                                <button
                                    key={color.hex}
                                    className={`filter-swatch ${isSelected ? 'filter-swatch--active' : ''}`}
                                    style={{
                                        background: color.hex,
                                        border: isLight ? '1px solid #ccc' : '1px solid transparent'
                                    }}
                                    onClick={() => setParam('colors', toggleInList(selectedColors, color.name))}
                                    title={color.name}
                                >
                                    {isSelected && (
                                        <span
                                            className="filter-swatch-tick"
                                            style={{ color: isLight ? '#333' : '#fff' }}
                                        >✓</span>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* sizes */}
            {(() => {
                // only show size groups that have at least one product with that size
                const availableSizes = new Set(products.flatMap(p => p.sizes || []))
                const activeGroups   = SIZE_GROUPS.filter(g => g.sizes.some(s => availableSizes.has(s)))
                if (activeGroups.length === 0) return null
                return (
                    <div className="filter-section">
                        <h3 className="filter-section-title">
                            Size
                            {selectedSizes.length > 0 && (
                                <span className="filter-active-count">{selectedSizes.length}</span>
                            )}
                        </h3>
                        {activeGroups.map(group => (
                            <div key={group.label} className="filter-size-group">
                                <p className="filter-size-group-label">{group.label}</p>
                                <div className="filter-size-grid">
                                    {group.sizes.filter(s => availableSizes.has(s)).map(size => (
                                        <button
                                            key={size}
                                            className={`filter-size-btn ${selectedSizes.includes(size) ? 'filter-size-btn--active' : ''}`}
                                            onClick={() => setParam('sizes', toggleInList(selectedSizes, size))}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            })()}

        </aside>
    )
}

export default Filters